import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LocalDate } from "@/components/ui/local-date";
import { SearchInput } from "@/components/ui/search-input";
import { api } from "@/lib/api";
import { useApiQuery } from "@/lib/use-api-query";
import { cn } from "@/lib/utils";
import { Link, Navigate, createFileRoute, useLoaderData } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Mail, Paperclip, RefreshCw } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

/** `/mail` — read-only view of the shared test mailbox (catch-all Prokop reads). */

type Address = { name: string | null; address: string | null };

type ListResponse = {
  configured: boolean;
  address: string | null;
  domain: string | null;
  total: number;
  page: number;
  pageSize: number;
  messages: Array<{
    uid: number;
    subject: string | null;
    from: Address | null;
    to: Address[];
    date: string | null;
    hasAttachments: boolean;
  }>;
  error?: string;
};

type Message = {
  uid: number;
  subject: string | null;
  from: Address[];
  to: Address[];
  cc: Address[];
  replyTo: Address[];
  date: string | null;
  text: string | null;
  html: string | null;
  attachments: Array<{ index: number; filename: string | null; contentType: string; size: number }>;
};

const searchSchema = z.object({
  page: z.number().int().positive().optional(),
  to: z.string().optional(),
  uid: z.number().int().positive().optional(),
});

export const Route = createFileRoute("/_app/mail")({
  validateSearch: searchSchema,
  component: MailPage,
});

function fmt(a: Address | null | undefined): string {
  if (!a) return "";
  if (a.name && a.address) return `${a.name} <${a.address}>`;
  return a.name ?? a.address ?? "";
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function MailPage() {
  const { isCustomer } = useLoaderData({ from: "/_app" });
  const search = Route.useSearch();
  const page = search.page ?? 1;
  const to = search.to?.trim() ?? "";
  const list = useApiQuery(
    () =>
      api.get<ListResponse>(`/api/mail?page=${page}${to ? `&to=${encodeURIComponent(to)}` : ""}`),
    [page, to],
    { enabled: !isCustomer },
  );

  if (isCustomer) return <Navigate to="/" />;

  const data = list.data;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Mail className="h-6 w-6" /> Test mail
          </h1>
          <p className="text-sm text-muted-foreground">
            {data?.address ?? "Test mailbox"}
            {data?.domain ? ` · everything sent to *@${data.domain} lands here` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-64">
            <SearchInput placeholder="Recipient…" paramName="to" />
          </div>
          <Button variant="outline" size="sm" onClick={list.reload} disabled={list.loading}>
            <RefreshCw className={cn("h-4 w-4", list.loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {data && !data.configured && (
        <p className="rounded-md border p-4 text-sm text-muted-foreground">
          The test mailbox isn't configured (TESTER_MAIL_ADDRESS / TESTER_MAIL_PASS).
        </p>
      )}
      {(list.error || data?.error) && (
        <p className="rounded-md border border-destructive/50 p-4 text-sm text-destructive">
          Couldn't read the mailbox: {data?.error ?? list.error}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="rounded-md border">
          <ul className="divide-y" data-testid="mail-list">
            {data?.messages.map((m) => (
              <li key={m.uid}>
                <Link
                  to="/mail"
                  search={{ ...search, uid: m.uid }}
                  className={cn(
                    "block px-3 py-2 hover:bg-muted/60",
                    search.uid === m.uid && "bg-muted",
                  )}
                >
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="truncate">{fmt(m.from)}</span>
                    {m.date && <LocalDate value={m.date} withTime className="shrink-0" />}
                  </div>
                  <div className="flex items-center gap-1 truncate text-sm font-medium">
                    {m.hasAttachments && <Paperclip className="h-3.5 w-3.5 shrink-0" />}
                    <span className="truncate">{m.subject || "(no subject)"}</span>
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    to {m.to.map((a) => a.address).join(", ")}
                  </div>
                </Link>
              </li>
            ))}
            {data?.configured && !data.error && data.messages.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-muted-foreground">No messages.</li>
            )}
          </ul>
          {data && data.total > 0 && (
            <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
              <span>
                {data.total} messages · page {page} / {pages}
              </span>
              <div className="flex gap-1">
                <Link
                  to="/mail"
                  search={{ ...search, page: page - 1 }}
                  disabled={page <= 1}
                  className={cn("rounded p-1 hover:bg-muted", page <= 1 && "opacity-40")}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Link>
                <Link
                  to="/mail"
                  search={{ ...search, page: page + 1 }}
                  disabled={page >= pages}
                  className={cn("rounded p-1 hover:bg-muted", page >= pages && "opacity-40")}
                >
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="min-w-0">
          {search.uid ? (
            <MessageView uid={search.uid} />
          ) : (
            <p className="rounded-md border p-6 text-center text-sm text-muted-foreground">
              Pick a message.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function MessageView({ uid }: { uid: number }) {
  const { data, loading, error } = useApiQuery(() => api.get<Message>(`/api/mail/${uid}`), [uid]);
  const [mode, setMode] = useState<"html" | "text">("html");

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!data || loading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const showHtml = mode === "html" && data.html;
  const rows: Array<[string, Address[]]> = [
    ["From", data.from],
    ["To", data.to],
    ["Cc", data.cc],
    ["Reply-To", data.replyTo],
  ];

  return (
    <div className="space-y-3 rounded-md border p-4" data-testid="mail-message">
      <h2 className="text-lg font-semibold">{data.subject || "(no subject)"}</h2>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
        {rows
          .filter(([, list]) => list.length > 0)
          .map(([label, list]) => (
            <div key={label} className="contents">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="break-all">{list.map(fmt).join(", ")}</dd>
            </div>
          ))}
        {data.date && (
          <>
            <dt className="text-muted-foreground">Date</dt>
            <dd>
              <LocalDate value={data.date} withTime />
            </dd>
          </>
        )}
      </dl>

      {data.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {data.attachments.map((a) => (
            <a key={a.index} href={`/api/mail/${uid}/attachments/${a.index}`} download>
              <Badge variant="outline" className="gap-1">
                <Paperclip className="h-3 w-3" />
                {a.filename ?? `attachment ${a.index + 1}`} · {fmtSize(a.size)}
              </Badge>
            </a>
          ))}
        </div>
      )}

      {data.html && data.text && (
        <div className="flex gap-1">
          {(["html", "text"] as const).map((m) => (
            <Button
              key={m}
              size="sm"
              variant={mode === m ? "secondary" : "ghost"}
              onClick={() => setMode(m)}
            >
              {m === "html" ? "HTML" : "Text"}
            </Button>
          ))}
        </div>
      )}

      {showHtml ? (
        // Empty sandbox: no scripts, no same-origin access to Hub.
        <iframe
          title="Message"
          sandbox=""
          srcDoc={data.html ?? ""}
          className="h-[70vh] w-full rounded border bg-white"
        />
      ) : (
        <pre className="whitespace-pre-wrap break-words font-sans text-sm">
          {data.text || "(empty)"}
        </pre>
      )}
    </div>
  );
}
